import type { ReactNode } from 'react';

import type { DateInput } from '@repo/common';
import { formatTimeISO, isBeforeDay, isSameDay } from '@repo/common';
import { usePeriodCalendar } from '@repo/ui/fixtures/smallCalendar/hooks/use-calendar';
import { getNumberOfTheMonthLabel } from '@repo/ui/utils/time/month-time';

import { MonthlyCalendarDay } from '../MonthlyCalendarDay/MonthlyCalendarDay';

import './MonthlyCalendarDaysGrid.scss';

interface MonthlyCalendarDaysGridProps {
  onDateSelect: (date: DateInput) => void;
  /** Lets past days be picked; by default they are disabled. */
  allowPastDates?: boolean;
  renderDayMarker?: (day: Date) => ReactNode;
}

export function MonthlyCalendarDaysGrid({
  onDateSelect,
  allowPastDates = false,
  renderDayMarker,
}: MonthlyCalendarDaysGridProps) {
  const { currentPeriodDays, selectDay, selectedDay } = usePeriodCalendar();
  const today = new Date();
  const currentMonth = currentPeriodDays[1][0]?.getMonth();

  const daysToDisplay = currentPeriodDays.flat();

  return (
    <div className="MonthlyCalendarDaysGrid">
      {daysToDisplay.map(day => {
        const isCurrentMonth = day.getMonth() === currentMonth;
        const isToday = isSameDay(day, today);
        const isPastDate = !allowPastDates && isBeforeDay(day, today);

        return (
          <MonthlyCalendarDay
            key={formatTimeISO(day)}
            day={day}
            monthDay={getNumberOfTheMonthLabel(day)}
            onClick={() => {
              if (isCurrentMonth && !isPastDate) {
                selectDay(day);
                onDateSelect(day); // Notify the parent about the date selection
              }
            }}
            isSelected={isSameDay(day, selectedDay)}
            isCurrentMonth={isCurrentMonth}
            isToday={isToday}
            isInactive={isPastDate}
            marker={isCurrentMonth ? renderDayMarker?.(day) : undefined}
          />
        );
      })}
    </div>
  );
}
